import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

class AccordionCustom extends HTMLElement {
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');
    return details;
  }

  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');
    return summary;
  }

  get content() {
    const content = this.details.querySelector('.details-content');
    if (!(content instanceof HTMLElement)) throw new Error('Details content not found');
    return content;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #isAnimating = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#setInitialStyles();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.#handleSummaryClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #setInitialStyles() {
    if (this.details.open) {
      this.content.style.height = 'auto';
    } else {
      this.content.style.height = '0px';
      this.content.style.overflow = 'hidden';
    }
  }

  #handleSummaryClick = async (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    if (this.#isAnimating) return;

    if (this.details.open) {
      await this.close();
    } else {
      await this.open();
    }
  };

  async open() {
    if (this.#isAnimating || this.details.open) return;

    this.#isAnimating = true;

    const parent = this.parentElement;
    if (parent) {
      const accordions = parent.querySelectorAll('accordion-custom');
      for (const accordion of accordions) {
        if (accordion !== this && typeof accordion.close === 'function') {
          await accordion.close();
        }
      }
    }

    this.details.open = true;

    const content = this.content;
    content.style.overflow = 'hidden';
    content.style.height = '0px';

    requestAnimationFrame(() => {
      content.style.transition = 'height 0.3s ease';
      content.style.height = `${content.scrollHeight}px`;
    });

    const onEnd = () => {
      content.style.height = 'auto';
      content.style.transition = '';
      content.style.overflow = '';
      content.removeEventListener('transitionend', onEnd);
      this.#isAnimating = false;
    };

    content.addEventListener('transitionend', onEnd);
  }

  async close() {
    if (this.#isAnimating || !this.details.open) return;

    this.#isAnimating = true;

    const content = this.content;
    content.style.overflow = 'hidden';
    content.style.height = `${content.scrollHeight}px`;

    requestAnimationFrame(() => {
      content.style.transition = 'height 0.3s ease';
      content.style.height = '0px';
    });

    const onEnd = () => {
      this.details.open = false;
      content.style.transition = '';
      content.style.height = '0px';
      content.removeEventListener('transitionend', onEnd);
      this.#isAnimating = false;
    };

    content.addEventListener('transitionend', onEnd);
  }

  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    this.#setInitialStyles();
  };

  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();
      this.close();
      this.summary.focus();
    }
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}